// @effect-diagnostics globalDate:off -- non-Effect debug timing helper called from UI code; plain wall-clock timing.
// @effect-diagnostics globalConsole:off -- non-Effect debug helper; intentionally logs to the browser console.
/**
 * Client-side turn latency timing (debug). Records when the user SENT a turn and
 * logs the perceived legs — send → first assistant event → completion — to the
 * browser console as `[client-timing]`. Pairs with the backend `[turn-timeline]`
 * trace: the gap between this COMPLETE total and the backend `total_ms` is the
 * client↔server pickup + WS delivery overhead (the part the server can't see).
 *
 * Keyed by threadId (a user runs one turn at a time per thread). Pure logging +
 * an in-memory map; no app state, safe to strip later.
 */

interface TurnMarks {
  readonly sendAt: number;
  firstEventAt?: number;
}

const byThread = new Map<string, TurnMarks>();

function log(message: string): void {
  // eslint-disable-next-line no-console
  if (typeof console !== "undefined") console.info(message);
}

/** Call when a turn is dispatched from the composer (startThreadTurn). */
export function markTurnSend(threadId: string): void {
  byThread.set(threadId, { sendAt: Date.now() });
  log(`[client-timing] thread=${threadId} SEND`);
}

/** Call on the first assistant event for the thread (first token / stream start). */
export function markTurnFirstEvent(threadId: string): void {
  const marks = byThread.get(threadId);
  if (!marks || marks.firstEventAt !== undefined) return;
  marks.firstEventAt = Date.now();
  log(`[client-timing] thread=${threadId} first-event +${marks.firstEventAt - marks.sendAt}ms`);
}

/** Call when the assistant turn completes (streaming → false). Logs + clears. */
export function markTurnComplete(threadId: string): void {
  const marks = byThread.get(threadId);
  if (!marks) return;
  const now = Date.now();
  const firstLeg =
    marks.firstEventAt !== undefined ? ` first-event=${marks.firstEventAt - marks.sendAt}ms` : "";
  log(`[client-timing] thread=${threadId} COMPLETE total=${now - marks.sendAt}ms${firstLeg}`);
  byThread.delete(threadId);
}
